import React, { useState, useEffect, useRef } from 'react';
import { User, Mail, Phone, MapPin, Bell, Shield, Key, Globe, Moon, UserCircle, Camera, Calendar, FileText, Star, CheckCircle, XCircle, AlertCircle, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getResourceByAuthId, Resource } from '../lib/resources';
import { useBusiness } from '../contexts/BusinessContext';
import EarningsReport from '../components/EarningsReport';
import TimeOffRequestModal from '../components/TimeOffRequestModal';
import { format } from 'date-fns';
// Removed usePendingTimeOffCount hook - now handled directly in LeftNavigation

interface UserInfo {
  firstName: string;
  lastName: string;
  email: string;
  isAdmin: boolean;
  role?: string;
  resourceId?: string;
  avatarUrl?: string;
}

interface TimeOffRequest {
  id: string;
  start_date: string;
  end_date: string;
  request_type: string;
  is_partial_day: boolean;
  start_time?: string;
  end_time?: string;
  notes?: string;
  status: 'pending' | 'approved' | 'denied';
  requested_at: string;
  approved_by?: string;
  approved_at?: string;
  reason_for_denial?: string;
  business_id: string;
  resource_id: string;
}

interface StarRatingProps {
  rating: number;
  size?: 'small' | 'medium' | 'large';
  showValue?: boolean;
}

const StarRating: React.FC<StarRatingProps> = ({ rating, size = 'medium', showValue = false }) => {
  const sizeClasses = {
    small: 'h-3 w-3',
    medium: 'h-4 w-4',
    large: 'h-5 w-5'
  };

  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;

  return (
    <div className="flex items-center space-x-1">
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${sizeClasses[size]} ${
              star <= fullStars
                ? 'text-yellow-400 fill-current'
                : star === fullStars + 1 && hasHalfStar
                ? 'text-yellow-400 fill-current opacity-50'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
      {showValue && (
        <span className="text-xs sm:text-sm font-medium text-gray-700 ml-1">{rating.toFixed(1)}</span>
      )}
    </div>
  );
};

export default function Profile() {
  const { selectedBusiness } = useBusiness();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [resourceRating, setResourceRating] = useState<{
    rating: number;
    feedbackCount: number;
  } | null>(null);
  const [isLoadingRating, setIsLoadingRating] = useState(false);
  const [isTimeOffModalOpen, setIsTimeOffModalOpen] = useState(false);
  const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[]>([]);
  const [isLoadingTimeOff, setIsLoadingTimeOff] = useState(false);
  const [timeOffDisplayLimit, setTimeOffDisplayLimit] = useState(3);

  // Hook for pending time off count (for navigation badge)
  // Navigation badge is now handled directly in LeftNavigation component

  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const { data: { session } } = await supabase!.auth.getSession();
        if (!session?.user) return;

        // Check if user is admin (business owner)
        const { data: businessData } = await supabase!
          .from('businesses')
          .select('id')
          .eq('owner_id', session.user.id)
          .maybeSingle();

        if (businessData) {
          setUserInfo({
            firstName: 'ADMIN',
            lastName: '',
            email: session.user.email || '',
            isAdmin: true
          });
          return;
        }

        // If not admin, get resource information with new permission role
        const { data: resourceData, error: resourceError } = await supabase!
          .from('resources')
          .select(`
            *,
            role:resource_roles(
              id,
              name,
              description
            )
          `)
          .eq('auth_user_id', session.user.id)
          .single();

        if (resourceError) {
          console.error('Error getting resource:', resourceError);
          return;
        }

        if (resourceData) {
          setUserInfo({
            firstName: resourceData.first_name || '',
            lastName: resourceData.last_name || '',
            email: resourceData.email || '',
            isAdmin: false,
            role: resourceData.role?.name || resourceData.role || 'Resource',
            resourceId: resourceData.id,
            avatarUrl: resourceData.avatar_url
          });
        }
      } catch (error) {
        console.error('Error loading user info:', error);
      }
    };

    loadUserInfo();
  }, []);

  // Load resource rating when userInfo changes
  useEffect(() => {
    if (userInfo?.resourceId && selectedBusiness?.id && !userInfo.isAdmin) {
      loadResourceRating();
      loadTimeOffRequests();

      // Set up real-time subscription for this user's time off requests
      if (!supabase) return;
      
      const timeOffSubscription = supabase
        .channel('profile_time_off_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'time_off_requests',
            filter: `resource_id=eq.${userInfo.resourceId}`
          },
          (payload) => {
            // Reload time off requests when any change occurs to this user's requests
            loadTimeOffRequests();
          }
        )
        .subscribe();

      return () => {
        if (supabase) {
          supabase.removeChannel(timeOffSubscription);
        }
      };
    }
  }, [userInfo?.resourceId, selectedBusiness?.id]);

  // Function to load 30-day rolling resource rating
  const loadResourceRating = async () => {
    if (!userInfo?.resourceId || !selectedBusiness?.id || !supabase) return;
    
    setIsLoadingRating(true);
    try {
      // Calculate 30 days ago from today
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const startDate = thirtyDaysAgo.toISOString();
      const endDate = new Date().toISOString();

      // Get feedback for this resource from the last 30 days
      const { data: feedbackData, error } = await supabase
        .from('booking_feedback')
        .select(`
          *,
          bookings!inner(
            id,
            business_id,
            booking_resources!inner(
              resource_id
            )
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('bookings.booking_resources.resource_id', userInfo.resourceId)
        .eq('status', 'submitted')
        .gte('submitted_at', startDate)
        .lte('submitted_at', endDate);

      if (error) {
        console.error('Error fetching resource rating:', error);
        return;
      }

      if (!feedbackData || feedbackData.length === 0) {
        setResourceRating({ rating: 0, feedbackCount: 0 });
        return;
      }

      // Calculate average rating using both dynamic form responses and legacy fields
      let totalRating = 0;
      let totalRatingCount = 0;

      feedbackData.forEach(feedback => {
        let feedbackAverage = 0;
        let ratingCount = 0;

        if (feedback.form_responses && Object.keys(feedback.form_responses).length > 0) {
          // Use dynamic form responses
          Object.values(feedback.form_responses).forEach(value => {
            if (typeof value === 'number' && value > 0) {
              feedbackAverage += value;
              ratingCount++;
        }
          });
        } else {
          // Fallback to legacy fields
          if (feedback.overall_quality_rating) {
            feedbackAverage += feedback.overall_quality_rating;
            ratingCount++;
          }
          if (feedback.overall_experience_rating) {
            feedbackAverage += feedback.overall_experience_rating;
            ratingCount++;
          }
        }

        if (ratingCount > 0) {
          totalRating += feedbackAverage / ratingCount;
          totalRatingCount++;
        }
      });

      const averageRating = totalRatingCount > 0 ? totalRating / totalRatingCount : 0;
      setResourceRating({
        rating: averageRating,
        feedbackCount: feedbackData.length
      });

    } catch (error) {
      console.error('Error loading resource rating:', error);
    } finally {
      setIsLoadingRating(false);
    }
  };

  // Function to load time off requests for the resource
  const loadTimeOffRequests = async () => {
    if (!userInfo?.resourceId || !selectedBusiness?.id || !supabase) return;

    setIsLoadingTimeOff(true);
    try {
      const { data: requests, error } = await supabase
        .from('time_off_requests')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .eq('resource_id', userInfo.resourceId)
        .order('requested_at', { ascending: false })
        .limit(5); // Show only the 5 most recent requests

      if (error) {
        console.error('Error fetching time off requests:', error);
        return;
        }
        
      setTimeOffRequests(requests || []);
    } catch (error) {
      console.error('Error loading time off requests:', error);
    } finally {
      setIsLoadingTimeOff(false);
    }
  };

  // Function to cancel a time off request
  const handleCancelRequest = async (requestId: string) => {
    if (!supabase) return;

    try {
      const { error } = await supabase
        .from('time_off_requests')
        .delete()
        .eq('id', requestId)
        .eq('resource_id', userInfo?.resourceId); // Ensure only the owner can cancel

      if (error) {
        console.error('Error canceling time off request:', error);
        return;
      }

      // Refresh the requests list
      loadTimeOffRequests();
      // Navigation badge will update automatically via real-time subscription
    } catch (error) {
      console.error('Error canceling time off request:', error);
    }
  };

  // Add the handleProfilePhotoUpload function
  const handleProfilePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !supabase || !userInfo?.resourceId) return;

    try {
      setIsUploading(true);

      // Upload the file to Supabase storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${userInfo.resourceId}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      // Update the resource's avatar_url
      const { error: updateError } = await supabase
        .from('resources')
        .update({ avatar_url: publicUrl })
        .eq('id', userInfo.resourceId);

      if (updateError) throw updateError;

      // Update local state
      setUserInfo(prev => prev ? { ...prev, avatarUrl: publicUrl } : null);
    } catch (error) {
      console.error('Error uploading profile photo:', error);
    } finally {
      setIsUploading(false);
    }
  };

  if (!userInfo) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      }}>
        <div className="text-center bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-lg p-8" style={{
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)'
        }}>
          <div className="w-12 h-12 border-3 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto shadow-lg shadow-purple-600/30"></div>
          <p className="mt-4 text-base text-gray-600 font-medium">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col pb-4 h-full overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen" style={{ 
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      paddingTop: 'env(safe-area-inset-top, 0px)',
      paddingBottom: 'env(safe-area-inset-bottom, 0px)'
    }}>
      {/* Header Section */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-gray-200/50 px-3 sm:px-4 md:px-8 py-3 sm:py-4 md:py-6 sticky top-0 z-40" style={{
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)'
      }}>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-2 md:space-y-0">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">Profile</h1>
            <p className="text-xs sm:text-sm md:text-base text-gray-600 mt-1">
              Manage your account settings and view earnings
            </p>
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="flex-1 px-3 sm:px-4 md:px-8 py-4 sm:py-6">
        <div className="space-y-4 sm:space-y-6">
            {/* Profile Header */}
          <div className="bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-lg p-4 sm:p-6" style={{
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)'
          }}>
            {/* Mobile Layout - Stack vertically */}
            <div className="flex flex-col sm:flex-row sm:items-center">
              {/* Avatar and basic info */}
              <div className="flex items-center justify-center sm:justify-start">
                <label className="relative cursor-pointer group">
                  <div className="h-20 w-20 sm:h-16 sm:w-16 rounded-xl bg-gradient-to-br from-purple-600 to-purple-700 flex items-center justify-center overflow-hidden shadow-sm">
                    {userInfo.avatarUrl ? (
                      <img 
                        src={userInfo.avatarUrl} 
                        alt="Profile" 
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <User className="h-10 w-10 sm:h-8 sm:w-8 text-white" />
                    )}
                  </div>
                  {!userInfo.isAdmin && (
                    <>
                      <div className="absolute inset-0 rounded-xl bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <Camera className="h-5 w-5 text-white" />
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleProfilePhotoUpload}
                    disabled={isUploading}
                  />
                    </>
                  )}
                  {isUploading && (
                    <div className="absolute inset-0 rounded-xl bg-black bg-opacity-50 flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    </div>
                  )}
                </label>
              </div>

              {/* User info - Center on mobile, left on desktop */}
              <div className="mt-4 sm:mt-0 sm:ml-6 text-center sm:text-left flex-1">
                <div className="flex flex-col sm:flex-row sm:items-center justify-center sm:justify-start gap-2 sm:gap-3">
                  <div className="text-xl sm:text-xl md:text-2xl font-bold text-gray-900">
                    {userInfo?.isAdmin ? 'ADMIN' : `${userInfo?.firstName} ${userInfo?.lastName}`}
                  </div>
                  {/* Star Rating - Only show for non-admin users */}
                  {!userInfo.isAdmin && resourceRating && resourceRating.feedbackCount > 0 && (
                    <div className="flex items-center justify-center sm:justify-start gap-2">
                      <StarRating rating={resourceRating.rating} size="small" showValue />
                      <span className="text-xs text-gray-500">
                        ({resourceRating.feedbackCount} review{resourceRating.feedbackCount !== 1 ? 's' : ''})
                      </span>
                  </div>
                  )}
                  {/* Loading state for rating */}
                  {!userInfo.isAdmin && isLoadingRating && (
                    <div className="flex items-center justify-center sm:justify-start">
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-purple-600 rounded-full animate-spin"></div>
                      <span className="text-xs text-gray-500 ml-2">Loading rating...</span>
                </div>
                  )}
                  {/* No rating message */}
                  {!userInfo.isAdmin && resourceRating && resourceRating.feedbackCount === 0 && !isLoadingRating && (
                    <div className="text-xs text-gray-500">
                      No reviews in last 30 days
              </div>
                  )}
            </div>
                <div className="text-sm sm:text-sm md:text-base text-gray-600 font-medium mt-1">
                  {userInfo?.isAdmin ? 'Administrator' : userInfo?.role || 'Resource'}
                  </div>
                <div className="text-sm text-gray-500 mt-2 flex items-center justify-center sm:justify-start">
                  <Mail className="h-4 w-4 mr-2" />
                  <span className="break-all">{userInfo?.email}</span>
                </div>

                {/* Compact Action Buttons - Only for non-admin users */}
                {!userInfo.isAdmin && (
                  <div className="flex gap-2 sm:gap-3 mt-4 justify-center sm:justify-start">
                    {/* Time Off Button */}
                  <button
                      onClick={() => setIsTimeOffModalOpen(true)}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 hover:text-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-200"
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Time Off
                  </button>
                  
                    {/* View Paystubs Button */}
                    <button className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 hover:text-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200">
                      <FileText className="h-4 w-4 mr-2" />
                      Paystubs
                          </button>
                    </div>
                  )}
                    </div>
                        </div>
                        </div>


          {/* Detailed Earnings Report */}
          {!userInfo.isAdmin && userInfo.resourceId && (
            <EarningsReport userInfo={{ 
              resourceId: userInfo.resourceId,
              firstName: userInfo.firstName,
              lastName: userInfo.lastName,
              email: userInfo.email,
              avatarUrl: userInfo.avatarUrl 
            }} />
          )}

          {/* Time Off Requests Section */}
          {!userInfo.isAdmin && userInfo.resourceId && (
            <div className="bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-lg p-3 sm:p-4 md:p-6" style={{
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)'
            }}>
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                    <div>
                  <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900">Recent Time Off Requests</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1">Your latest time off submissions</p>
                    </div>
                  </div>

              {isLoadingTimeOff ? (
                <div className="flex items-center justify-center py-6 sm:py-8">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                  <span className="ml-2 sm:ml-3 text-sm sm:text-base text-gray-600">Loading requests...</span>
                </div>
              ) : timeOffRequests.length > 0 ? (
                <div className="space-y-2.5 sm:space-y-3">
                  {timeOffRequests.slice(0, timeOffDisplayLimit).map((request) => {
                    const statusConfig = {
                      pending: { icon: AlertCircle, color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200' },
                      approved: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' },
                      denied: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' }
                    };
                    
                    const config = statusConfig[request.status];
                    const StatusIcon = config.icon;
                    
                    const formatDate = (dateStr: string) => {
                      try {
                        // Parse as local date to avoid timezone shifting
                        const [year, month, day] = dateStr.split('-').map(Number);
                        const localDate = new Date(year, month - 1, day); // month is 0-indexed
                        return format(localDate, 'MMM dd, yyyy');
                      } catch {
                        return dateStr;
                      }
                    };

                    const getTypeLabel = (type: string) => {
                      const typeMap = {
                        vacation: 'Vacation',
                        sick: 'Sick Leave',
                        personal: 'Personal Day',
                        family: 'Family Emergency',
                        bereavement: 'Bereavement',
                        other: 'Other'
                      };
                      return typeMap[type as keyof typeof typeMap] || type;
                    };

                          return (
                      <div key={request.id} className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl border ${config.bg} ${config.border} transition-all hover:shadow-md`}>
                        {/* Mobile Layout: Stack vertically */}
                        <div className="sm:hidden">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2 flex-1 min-w-0">
                              {request.status !== 'pending' && (
                                <StatusIcon className={`h-4 w-4 ${config.color} flex-shrink-0`} />
                              )}
                              <h4 className="text-sm font-semibold text-gray-900 truncate">
                                {getTypeLabel(request.request_type)}
                              </h4>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-md ${config.color} ${config.bg} capitalize flex-shrink-0`}>
                                {request.status}
                              </span>
                                  </div>
                                </div>
                          
                          <div className="space-y-2">
                            <div className="text-xs text-gray-600">
                              📅 {formatDate(request.start_date)} 
                              {request.start_date !== request.end_date && ` - ${formatDate(request.end_date)}`}
                              </div>

                            {request.is_partial_day && (
                              <div className="text-xs text-gray-600">
                                🕐 {request.start_time} - {request.end_time}
                                </div>
                              )}
                            
                            {request.notes && (
                              <p className="text-xs text-gray-500 line-clamp-2 bg-gray-50 p-2 rounded-lg">{request.notes}</p>
                      )}
                            
                            <div className="flex items-center justify-between pt-2">
                              <div className="text-xs text-gray-500">
                                Requested {format(new Date(request.requested_at), 'MMM dd')}
                              </div>
                              {/* Cancel button for mobile */}
                              {(request.status === 'pending' || request.status === 'approved') && (
                                <button
                                  onClick={() => {
                                    if (window.confirm('Are you sure you want to cancel this time off request?')) {
                                      handleCancelRequest(request.id);
                                    }
                                  }}
                                  className="inline-flex items-center px-3 py-2 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 hover:text-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 transition-all duration-200 touch-manipulation"
                                  title="Cancel request"
                                >
                                  <Trash2 className="h-3 w-3 mr-1.5" />
                                  Cancel
                                </button>
                              )}
                                  </div>
                                </div>
                              </div>

                        {/* Desktop Layout: Side-by-side */}
                        <div className="hidden sm:flex items-center justify-between">
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            {request.status !== 'pending' && (
                              <StatusIcon className={`h-5 w-5 ${config.color} flex-shrink-0`} />
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <h4 className="text-sm font-semibold text-gray-900 truncate">
                                  {getTypeLabel(request.request_type)}
                                </h4>
                                <span className={`px-2 py-1 text-xs font-medium rounded-lg ${config.color} ${config.bg} capitalize`}>
                                  {request.status}
                                              </span>
                                </div>
                              <div className="text-xs text-gray-600">
                                {formatDate(request.start_date)} 
                                {request.start_date !== request.end_date && ` - ${formatDate(request.end_date)}`}
                                {request.is_partial_day && (
                                  <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md">
                                    {request.start_time} - {request.end_time}
                                  </span>
                              )}
                            </div>
                              {request.notes && (
                                <p className="text-xs text-gray-500 mt-1 line-clamp-2">{request.notes}</p>
                      )}
                    </div>
                    </div>
                          <div className="flex items-center space-x-3 ml-3 flex-shrink-0">
                            <div className="text-xs text-gray-500">
                              {format(new Date(request.requested_at), 'MMM dd')}
                  </div>
                            {/* Cancel button for desktop */}
                            {(request.status === 'pending' || request.status === 'approved') && (
                              <button
                                onClick={() => {
                                  if (window.confirm('Are you sure you want to cancel this time off request?')) {
                                    handleCancelRequest(request.id);
                                  }
                                }}
                                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 hover:text-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 transition-all duration-200"
                                title="Cancel request"
                              >
                                <Trash2 className="h-3 w-3 mr-1" />
                                Cancel
                              </button>
                )}
              </div>
            </div>
          </div>
                    );
                  })}
        
                  {/* View More Button */}
                  {timeOffRequests.length > timeOffDisplayLimit && (
                    <div className="mt-4 text-center">
                      <button
                        onClick={() => setTimeOffDisplayLimit(prev => prev + 3)}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
                      >
                        View More ({timeOffRequests.length - timeOffDisplayLimit} remaining)
                      </button>
                    </div>
                  )}
        </div>
              ) : (
                <div className="text-center py-6 sm:py-8">
                  <Calendar className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                  <h4 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No Time Off Requests</h4>
                  <p className="text-sm sm:text-base text-gray-500 max-w-sm mx-auto px-4">
                    You haven't submitted any time off requests yet. Click the "Time Off" button above to create your first request.
                  </p>
      </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Time Off Request Modal */}
      {userInfo?.resourceId && (
        <TimeOffRequestModal
          isOpen={isTimeOffModalOpen}
          onClose={() => setIsTimeOffModalOpen(false)}
          onSuccess={() => {
            setIsTimeOffModalOpen(false);
            loadTimeOffRequests(); // Refresh the requests list
            // Navigation badge will update automatically via real-time subscription
          }}
          resourceId={userInfo.resourceId}
        />
      )}
    </div>
  );
} 